import { Controller } from 'stimulus';

// Carries the active "compare" selection across period / date-range changes.
//
// The period header (24h/7d/30d shortcuts + custom-range form) is rendered in
// the page heading, OUTSIDE each dashboard's chart controller. The chart
// controller (see `compare_chart_base.js`) keeps the current comparison in the
// URL via `history.replaceState`, but it can't reach these links/form to wire
// the value onto them. Without help, ticking compare checkboxes and then
// switching period (or applying a custom range) sends a request with no
// `compare` value and the selection is lost.
//
// This controller reads the live `compare` param from `window.location` at
// navigation time and merges it into:
//   - each shortcut link's `href` (a plain GET navigation), and
//   - the custom-range form's `action` query. The form POSTs to the
//     WithDateRange `:create` action, whose `redirect_url` forwards
//     `request.GET` (the action URL's query) — NOT POST body params — so the
//     value has to ride on the action URL, not a hidden field, to survive the
//     redirect.
//
// On tabs without a comparison feature `compare` is simply absent and every
// rewrite is a no-op.
export default class extends Controller {
  connect() {
    this._onClick = this.onClick.bind(this);
    this._onSubmit = this.onSubmit.bind(this);
    // Capture phase so we rewrite the target before the browser navigates.
    this.element.addEventListener('click', this._onClick, true);
    this.element.addEventListener('submit', this._onSubmit, true);
  }

  disconnect() {
    this.element.removeEventListener('click', this._onClick, true);
    this.element.removeEventListener('submit', this._onSubmit, true);
  }

  get compareParam() {
    return new URLSearchParams(window.location.search).get('compare');
  }

  onClick(event) {
    const link = event.target.closest('a[href]');
    if (!link || !this.element.contains(link)) return;
    link.href = this.withCompare(link.href);
  }

  onSubmit(event) {
    const form = event.target.closest('form');
    if (!form || !this.element.contains(form)) return;
    form.action = this.withCompare(form.action);
  }

  // Merge the live `compare` value into a URL's query, replacing any stale
  // value and removing it entirely when nothing is selected.
  withCompare(href) {
    const url = new URL(href, window.location.origin);
    const compare = this.compareParam;
    if (compare) url.searchParams.set('compare', compare);
    else url.searchParams.delete('compare');
    return url.toString();
  }
}
